import {Button, Dialog, DialogActions, DialogContent, FormControlLabel, Stack, TextField, Typography} from '@mui/material'
import React, {memo, useCallback, useEffect, useRef, useState} from 'react'
import {makeStyles} from 'tss-react/mui'
import Checkbox from '@mui/material/Checkbox'
import DraggableDialogTitle from './DraggableDialogTitle'
import ClearIcon from "../../assets/icons/ClearIcon";
import {ExtraSmallButton} from "../custom";

type Props = {
    open: boolean
    onClose: () => void,
    onSubmit: (name: string, openInNewTab: boolean, createNew: boolean) => void,
    title: string,
    subTitle?: string,
    currentName?: string,
    showCheckbox?: boolean
    defaultOpenInNewTab?: boolean
}

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        '.MuiPaper-root': {
            padding: theme.spacing(0)
        }
    },
    contentContainer: {
        flexGrow: 1
    },
    titleBox: {
        padding: theme.spacing(0, 0, 1)
    },
    inputField: {
        '& .MuiInputBase-input': {
            padding: theme.spacing(1)
        }
    },
    subtitle: {
        padding: theme.spacing(2, 0)
    },
    buttonContainer: {
        margin: theme.spacing(2, 0, 0)
    },
    closeButtonBox: {
        marginTop: theme.spacing(1)
    },
    closeButton: {
        padding: theme.spacing(0.5),
        borderRadius: 0,
        marginRight: theme.spacing(0.5)
    },
    buttonSpacing: {
        marginRight: theme.spacing(2)
    }
}))

const NameDialog = ({
                        open,
                        onClose,
                        onSubmit,
                        currentName,
                        title,
                        subTitle,
                        showCheckbox,
                        defaultOpenInNewTab = true
                    }: Props) => {
    const [name, setName] = useState(currentName ?? '')
    const [mounted, setMounted] = useState(false)
    const [openInNewTab, setOpenInNewTab] = useState(defaultOpenInNewTab)
    const inputRef = useRef<HTMLInputElement>(null)
    const {classes} = useStyles()

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        setName(currentName ?? '')
    }, [currentName])

    useEffect(() => {
        if (mounted) {
            inputRef.current?.select()
        }
    }, [mounted])

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setName(event.target.value)
    }
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOpenInNewTab(event.target.checked)
    }

    const handleClose = useCallback(() => {
        onClose()
    }, [onClose])

    const handleSubmit = useCallback(() => {
        const trimmedName = name.trim()
        if (trimmedName) {
            onSubmit(trimmedName, openInNewTab, true)
            handleClose()
        }
    }, [handleClose, name, onSubmit, openInNewTab])

    const onKeyDown = useCallback((e: any) => {
        if (e.key === 'Enter') {
            handleSubmit()
        }
    }, [handleSubmit])

    return (
        <Dialog className={classes.dialogPaper} open={open} onClose={onClose} maxWidth={'xs'} fullWidth>
            <DraggableDialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}>{title}</Typography>
                    <ExtraSmallButton onClick={onClose} aria-label="close" color={"inherit"}>
                        <ClearIcon/>
                    </ExtraSmallButton>
                </Stack>
            </DraggableDialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={1}>
                    <Typography variant="body2">{subTitle}</Typography>
                    <TextField
                        size={"small"}
                        className={classes.inputField}
                        inputRef={inputRef}
                        variant="outlined"
                        fullWidth
                        multiline={false}
                        value={name}
                        onKeyDown={onKeyDown}
                        onChange={handleNameChange}
                        autoFocus
                        placeholder="My Layout"
                    />
                    {showCheckbox && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size={"small"}
                                    checked={openInNewTab}
                                    onChange={handleCheckboxChange}
                                    sx={{
                                        color: (theme) => theme.palette.primary.light,
                                        '&.Mui-checked': {
                                            color: (theme) => theme.palette.primary.main
                                        }
                                    }}
                                />
                            }
                            color="#d1d4dc"
                            label="Open in New Tab"
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleClose}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default memo(NameDialog)